import axios from 'axios'

// Cache local para otimizar requisições repetidas
const cache = {
  data: {},
  timeouts: {},
  get: (key) => {
    return cache.data[key] || null
  },
  set: (key, data, ttlSeconds = 30) => {
    if (cache.timeouts[key]) {
      clearTimeout(cache.timeouts[key])
    }
    cache.data[key] = data
    cache.timeouts[key] = setTimeout(() => {
      delete cache.data[key]
      delete cache.timeouts[key]
    }, ttlSeconds * 1000)
    return data
  },
  clear: (key = null) => {
    if (key === null) {
      Object.values(cache.timeouts).forEach(timeout => clearTimeout(timeout))
      cache.data = {}
      cache.timeouts = {}
    } else if (cache.timeouts[key]) {
      clearTimeout(cache.timeouts[key])
      delete cache.data[key]
      delete cache.timeouts[key]
    }
  }
}

// Configuração base do cliente Axios
const API_BASE_URL = window.__QUARNX_API_URL__ || import.meta.env.VITE_KERNEL_API || ''

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 8000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Interceptador de requisições
apiClient.interceptors.request.use(
  config => {
    // Tag each request with retry count
    config._retryCount = config._retryCount || 0
    return config
  },
  error => Promise.reject(error)
)

// Interceptador de respostas com retry automático para erros de rede
apiClient.interceptors.response.use(
  response => response,
  async (error) => {
    const config = error.config
    const MAX_RETRIES = 2
    const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')

    // Retry only GET requests on network errors (not POST to avoid duplicates)
    if (isNetworkError && config && config._retryCount < MAX_RETRIES && config.method === 'get') {
      config._retryCount += 1
      const delay = config._retryCount * 1000 // 1s, 2s
      await new Promise(resolve => setTimeout(resolve, delay))
      return apiClient(config)
    }

    // Silent logging — only once per 30s
    if (!window._apiErrorLogged) {
      window._apiErrorLogged = true
      if (error.response) {
        console.warn('[QuarnX API] Server error:', error.response.status)
      } else if (isNetworkError) {
        console.warn('[QuarnX API] Network unavailable after retries')
      }
      setTimeout(() => { window._apiErrorLogged = false }, 30000)
    }
    return Promise.reject(error)
  }
)

// Funções de API organizadas por domínio
export const ExplorerAPI = {
  // API de Blocos
  blocos: {
    obterUltimos: async (limite = 10, usarCache = true) => {
      const cacheKey = `blocos_ultimos_${limite}`
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get(`/api/v1/blocks?limit=${limite}`)
        let blocos = response.data
        if (response.data.blocks) blocos = response.data.blocks
        // Normalize field mappings to ensure required fields are present
        blocos = blocos.map(b => ({
          ...b,
          hash: b.hash || '',
          slot: b.slot ?? b.height ?? b.number ?? 0,
          previous_hash: b.previous_hash ?? b.previousHash ?? b.parent_hash ?? '',
          state_root: b.state_root ?? b.stateRoot ?? '',
          transactions: b.transactions ?? b.tx_count ?? 0,
          proposer: b.proposer ?? b.producer ?? b.validator ?? '',
        }))
        return cache.set(cacheKey, blocos, 10)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        throw new Error(`Falha ao obter últimos blocos: ${error.message}`)
      }
    },

    obterPorNumero: async (numero, usarCache = true) => {
      const cacheKey = `bloco_${numero}`
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        // Try fetching blocks around the target number
        // The kernel returns the latest N blocks, so we fetch enough to include our target
        const statsResp = await apiClient.get('/api/v1/stats')
        const currentHeight = statsResp.data.blockHeight || statsResp.data.height || 0
        const targetNum = Number(numero)
        
        // If the block is within the latest 50, fetch normally
        if (currentHeight - targetNum < 50) {
          const response = await apiClient.get(`/api/v1/blocks?limit=50`)
          const blocos = response.data.blocks || response.data || []
          const bloco = blocos.find(b => (b.slot ?? b.height ?? b.number) === targetNum)
          if (bloco) return cache.set(cacheKey, bloco, 60)
        }
        
        // For older blocks, use the sync endpoint which supports range queries
        try {
          const syncResp = await apiClient.get(`/api/v1/sync/blocks?from=${targetNum}&limit=1`)
          const syncBlocks = syncResp.data.blocks || syncResp.data || []
          if (syncBlocks.length > 0) {
            const bloco = syncBlocks[0]
            return cache.set(cacheKey, bloco, 60)
          }
        } catch (syncErr) {
          // sync endpoint may not support this query — fall through
        }
        
        throw new Error(`Bloco #${numero} não encontrado`)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        throw new Error(`Falha ao obter bloco #${numero}: ${error.message}`)
      }
    },

    obterTotal: async (usarCache = true) => {
      const cacheKey = 'blocos_total'
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData !== null) return cachedData
      }
      try {
        const response = await apiClient.get('/api/v1/stats')
        const total = response.data.blockHeight || response.data.height || 0
        return cache.set(cacheKey, total, 15)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData !== null) return cachedData
        throw new Error(`Falha ao obter total de blocos: ${error.message}`)
      }
    },
  },

  // API de Transações
  transacoes: {
    obterUltimas: async (limite = 10, usarCache = true) => {
      const cacheKey = `transacoes_ultimas_${limite}`
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get(`/api/v1/transactions/history?limit=${limite}`)
        let transacoes = response.data
        if (response.data.transactions) transacoes = response.data.transactions
        transacoes = transacoes.map(tx => ({
          ...tx,
          hash: tx.hash || '',
          from: tx.from || '',
          to: tx.to || '',
          value: tx.value ?? tx.amount ?? 0,
          fee: tx.fee ?? tx.taxaGas ?? tx.gasPrice ?? 0,
          status: tx.status || 'confirmada',
          valor: tx.valor || tx.amount || 0,
          moeda: tx.currency || (window.__QUARNX_COIN__ || 'tKinX'),
          taxaGas: tx.taxaGas || tx.gasPrice || 0,
        }))
        return cache.set(cacheKey, transacoes, 10)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        throw new Error(`Falha ao obter últimas transações: ${error.message}`)
      }
    },

    obterPorHash: async (hash, usarCache = true) => {
      const cacheKey = `transacao_${hash}`
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get(`/api/transactions/${hash}`)
        const tx = response.data
        if (tx) {
          tx.valor = tx.valor || tx.amount || 0
          tx.moeda = tx.currency || (window.__QUARNX_COIN__ || 'tKinX')
          tx.taxaGas = tx.taxaGas || tx.gasPrice || 0
        }
        return cache.set(cacheKey, tx, 60)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        throw new Error(`Falha ao obter transação ${hash}: ${error.message}`)
      }
    },

    enviar: async (transacao) => {
      try {
        const response = await apiClient.post('/api/v1/transactions', transacao)
        return response.data
      } catch (error) {
        console.error('Erro ao enviar transação:', error)
        throw error
      }
    }
  },

  // API de Contas
  contas: {
    obterPorEndereco: async (endereco) => {
      try {
        // Use the balance endpoint since there's no dedicated accounts endpoint
        const response = await apiClient.get(`/api/v1/balance/${endereco}`)
        return {
          address: endereco,
          balance: response.data.balance || 0,
          saldo: response.data.balance || 0,
          currency: response.data.currency || (window.__QUARNX_COIN__ || 'tKinX'),
          unit: response.data.unit || 'nanoKinX',
          ...response.data,
        }
      } catch (error) {
        console.error(`Erro ao buscar conta ${endereco}:`, error)
        throw error
      }
    },
    obterTransacoes: async (endereco, pagina = 1, limite = 20) => {
      try {
        // Use transaction history and filter client-side
        const response = await apiClient.get(`/api/v1/transactions/history?limit=50`)
        const allTxs = response.data.transactions || []
        const filtered = allTxs.filter(tx =>
          (tx.from || '').includes(endereco.substring(0, 16)) ||
          (tx.to || '').includes(endereco.substring(0, 16))
        )
        return { transactions: filtered.slice(0, limite) }
      } catch (error) {
        console.error(`Erro ao buscar transações da conta ${endereco}:`, error)
        throw error
      }
    },
    obterTokens: async (endereco) => {
      try {
        // No dedicated tokens-by-account endpoint — return empty
        return { tokens: [] }
      } catch (error) {
        console.error(`Erro ao buscar tokens da conta ${endereco}:`, error)
        throw error
      }
    }
  },

  // API de Validadores
  validadores: {
    obterTodos: async (usarCache = true) => {
      const cacheKey = 'validadores_todos'
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get('/api/v1/validators')
        let validadores = response.data
        if (response.data.validators) validadores = response.data.validators
        validadores = validadores.map((v) => ({
          ...v,
          address: v.address || '',
          name: v.name || `Validador ${(v.address || '').substring(0, 8)}`,
          stake: v.stake || v.amount || 0,
          merit_score: v.merit_score ?? v.meritScore ?? 0,
          status: v.status || 'Unknown',
          moeda: v.currency || (window.__QUARNX_COIN__ || 'tKinX')
        }))
        return cache.set(cacheKey, validadores, 30)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        throw new Error(`Falha ao obter validadores: ${error.message}`)
      }
    },

    obterPorEndereco: async (endereco) => {
      try {
        // Buscar validador individual via lista de validadores (kernel não tem endpoint individual)
        const response = await apiClient.get('/api/v1/validators')
        const validadores = response.data.validators || response.data || []
        const validador = validadores.find(v => (v.address || '').includes(endereco.substring(0, 16)))
        if (validador) return validador
        return { address: endereco, name: `Validador ${endereco.substring(0, 8)}`, stake: 0, status: 'Unknown' }
      } catch (error) {
        console.error(`Erro ao buscar validador ${endereco}:`, error)
        throw error
      }
    },

    obterEstatisticas: async () => {
      try {
        // Usar endpoint de stats geral (não existe endpoint específico de validadores)
        const response = await apiClient.get('/api/v1/stats')
        const stats = response.data || {}
        return {
          activeValidators: stats.activeValidators || 0,
          totalValidators: stats.totalValidators || 0,
          totalStake: 0,
        }
      } catch (error) {
        console.error('Erro ao buscar estatísticas dos validadores:', error)
        throw error
      }
    }
  },

  // API de Estatísticas
  estatisticas: {
    obterDashboard: async (usarCache = true) => {
      const cacheKey = 'estatisticas_dashboard'
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get('/api/v1/stats')
        const stats = response.data || {}
        const estatisticas = {
          altura: stats.blockHeight || stats.height || 0,
          transacoesPorSegundo: stats.tps || stats.transactionsPerSecond || 0,
          transacoesTotal: stats.totalTransactions || 0,
          tempoBloco: stats.blockTime || 2.5,
          dificuldade: stats.difficulty || 0,
          validadoresAtivos: stats.activeValidators || 0,
          validadoresTotal: stats.totalValidators || 0,
          gasPreco: stats.gasPrice || 5,
          marketcap: stats.marketCap || 0,
          moeda: stats.currency || (window.__QUARNX_COIN__ || 'tKinX'),
          currency: stats.currency || (window.__QUARNX_COIN__ || 'tKinX'),
          networkName: stats.network || 'QuarnX-Devnet',
          chainId: stats.chainId || 3,
          taxaCambio: stats.exchangeRate || 2.5,
          ultimoBloco: stats.latestBlock || null,
          ultimoValidador: stats.latestValidator || null,
          redeStatus: 'online'
        }
        return cache.set(cacheKey, estatisticas, 15)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        throw new Error(`Falha ao obter estatísticas do dashboard: ${error.message}`)
      }
    },

    obterConsenso: async () => {
      try {
        // Usar endpoint de stats geral (não existe endpoint específico de consenso)
        const response = await apiClient.get('/api/v1/stats')
        const stats = response.data || {}
        return {
          blockHeight: stats.blockHeight || 0,
          blockTime: stats.blockTime || 0.4,
          tps: stats.tps || 0,
          activeValidators: stats.activeValidators || 0,
        }
      } catch (error) {
        console.error('Erro ao buscar estatísticas de consenso:', error)
        throw error
      }
    }
  },

  // API para Desenvolvedor
  desenvolvedor: {
    obterTokens: async (endereco) => {
      try {
        // Buscar contratos deployados pelo endereço via lista de contratos
        const response = await apiClient.get('/api/v1/contracts')
        const contratos = response.data.contracts || []
        const tokens = contratos.filter(c => (c.deployer || '').includes(endereco.substring(0, 16)))
        return { tokens }
      } catch (error) {
        console.error('Erro ao buscar tokens do desenvolvedor:', error)
        throw error
      }
    },
    compilarContrato: async (codigo, linguagem = 'queen') => {
      try {
        const response = await apiClient.post('/api/v1/compile', { code: codigo, name: linguagem })
        return response.data
      } catch (error) {
        console.error('Erro ao compilar contrato:', error)
        throw error
      }
    },
    publicarContrato: async (bytecode, abi, parametros = []) => {
      try {
        const response = await apiClient.post('/api/v1/contracts/deploy', { code: bytecode, format: 'wasm', deployer: window.__QUARNX_WALLET_ADDR__ || '0'.repeat(64) })
        return response.data
      } catch (error) {
        console.error('Erro ao publicar contrato:', error)
        throw error
      }
    },
    consultarAssistente: async (pergunta, contexto = {}) => {
      // Assistente não disponível via API — respostas são geradas localmente
      return { response: 'Assistente indisponível. Use os templates para começar.' }
    }
  },

  // API para DEX (endpoints reais do kernel)
  dex: {
    obterPares: async () => {
      try {
        const response = await apiClient.get('/api/v1/dex/pairs')
        return response.data
      } catch (error) {
        return { pairs: [] }
      }
    },
    obterLivroDeOrdens: async (par) => {
      try {
        const response = await apiClient.get(`/api/v1/dex/orderbook/${encodeURIComponent(par)}`)
        return response.data
      } catch (error) {
        return { bids: [], asks: [] }
      }
    },
    obterNegociacoesRecentes: async (par, limite = 20) => {
      try {
        const response = await apiClient.get(`/api/v1/dex/trades/${encodeURIComponent(par)}`)
        return response.data
      } catch (error) {
        return { trades: [] }
      }
    },
    obterHistoricoPrecos: async (par, timeframe = '1h') => {
      try {
        // Usar trades recentes como proxy para histórico
        const response = await apiClient.get(`/api/v1/dex/trades/${encodeURIComponent(par)}`)
        return response.data
      } catch (error) {
        return { candles: [] }
      }
    },
    enviarOrdem: async (ordem) => {
      try {
        const response = await apiClient.post('/api/v1/dex/order', ordem)
        return response.data
      } catch (error) {
        console.error('Erro ao enviar ordem:', error)
        throw error
      }
    },
    cancelarOrdem: async (id) => {
      // Cancel não implementado no kernel — retornar sucesso local
      return { order_id: id, status: 'cancelled' }
    }
  },

  // API de Sistema
  sistema: {
    status: async (usarCache = true) => {
      const cacheKey = 'sistema_status'
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get('/api/v1/status')
        const statusData = response.data || {}
        const statusSistema = {
          apiConnected: true,
          blockchainConnected: statusData.connected !== false,
          blockHeight: statusData.blockHeight || statusData.height || 0,
          lastBlockTime: statusData.lastBlockTime || new Date().toISOString(),
          networkSynced: statusData.synced !== false,
          currentLoad: statusData.load || 0,
          memoryUsage: statusData.memory || 0,
          peers: statusData.peers || 0,
          version: statusData.version || '1.0.0',
          uptime: statusData.uptime || 0,
        }
        return cache.set(cacheKey, statusSistema, 5)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        return {
          apiConnected: false,
          blockchainConnected: false,
          blockHeight: 0,
          lastBlockTime: new Date().toISOString(),
          networkSynced: false,
          currentLoad: 0,
          memoryUsage: 0,
          peers: 0,
          version: '1.0.0',
          uptime: 0,
          error: error.message
        }
      }
    },

    atualizarValidadores: async () => {
      try {
        // Não existe endpoint dedicado — recarregar lista de validadores
        const response = await apiClient.get('/api/v1/validators')
        return response.data
      } catch (error) {
        console.error('Erro ao atualizar lista de validadores:', error)
        throw error
      }
    },

    limparCache: async () => {
      // Limpar cache local do frontend
      cache.clear()
      return { status: 'ok', message: 'Cache local limpo' }
    }
  },

  // API de Busca (não implementada no kernel — busca local)
  busca: {
    buscar: async (termo) => {
      try {
        // Tentar buscar em blocos e transações localmente
        const [blocosResp, txsResp] = await Promise.allSettled([
          apiClient.get('/api/v1/blocks?limit=50'),
          apiClient.get('/api/v1/transactions/history?limit=50'),
        ])
        const blocos = blocosResp.status === 'fulfilled' ? (blocosResp.value.data.blocks || []) : []
        const txs = txsResp.status === 'fulfilled' ? (txsResp.value.data.transactions || []) : []
        
        const termoLower = termo.toLowerCase()
        const resultados = {
          blocos: blocos.filter(b => 
            String(b.height || b.number || '').includes(termoLower) ||
            (b.hash || '').toLowerCase().includes(termoLower)
          ),
          transacoes: txs.filter(t =>
            (t.hash || '').toLowerCase().includes(termoLower) ||
            (t.from || '').toLowerCase().includes(termoLower) ||
            (t.to || '').toLowerCase().includes(termoLower)
          ),
        }
        return resultados
      } catch (error) {
        console.error(`Erro ao buscar por "${termo}":`, error)
        return { blocos: [], transacoes: [] }
      }
    }
  },

  // API de Contratos Inteligentes
  contratos: {
    listar: async () => {
      try {
        const response = await apiClient.get('/api/v1/contracts')
        return response.data
      } catch (error) {
        console.error('Erro ao listar contratos:', error)
        throw error
      }
    },
    obterPorEndereco: async (endereco) => {
      try {
        const response = await apiClient.get(`/api/v1/contracts/${endereco}`)
        return response.data
      } catch (error) {
        console.error(`Erro ao obter contrato ${endereco}:`, error)
        throw error
      }
    },
    deploy: async (code, format, deployer) => {
      try {
        const response = await apiClient.post('/api/v1/contracts/deploy', { code, format, deployer })
        return response.data
      } catch (error) {
        console.error('Erro ao deployar contrato:', error)
        throw error
      }
    },
    chamar: async (endereco, funcao, args, gasLimit) => {
      try {
        const body = { function: funcao, args, gas_limit: gasLimit }
        const response = await apiClient.post(`/api/v1/contracts/${endereco}/call`, body)
        return response.data
      } catch (error) {
        console.error('Erro ao chamar contrato:', error)
        throw error
      }
    },
    estimarGas: async (endereco, funcao, args) => {
      try {
        const response = await apiClient.post(`/api/v1/contracts/${endereco}/estimate`, { function: funcao, args })
        return response.data
      } catch (error) {
        console.error('Erro ao estimar gas:', error)
        throw error
      }
    }
  },

  // API de Governança
  governanca: {
    listarPropostas: async () => {
      try {
        const response = await apiClient.get('/api/v1/governance/proposals')
        return response.data
      } catch (error) {
        console.error('Erro ao listar propostas:', error)
        throw error
      }
    },
    criarProposta: async (title, description, proposalType, proposer, durationSecs) => {
      try {
        const response = await apiClient.post('/api/v1/governance/proposals', {
          title, description, proposal_type: proposalType, proposer, duration_secs: durationSecs
        })
        return response.data
      } catch (error) {
        console.error('Erro ao criar proposta:', error)
        throw error
      }
    },
    votar: async (proposalId, voter, vote) => {
      try {
        const response = await apiClient.post(`/api/v1/governance/proposals/${proposalId}/vote`, { voter, vote })
        return response.data
      } catch (error) {
        console.error('Erro ao votar:', error)
        throw error
      }
    },
    estatisticas: async () => {
      try {
        const response = await apiClient.get('/api/v1/governance/stats')
        return response.data
      } catch (error) {
        console.error('Erro ao obter stats de governança:', error)
        throw error
      }
    }
  },

  // API de Vesting
  vesting: {
    listarSchedules: async () => {
      try {
        const response = await apiClient.get('/api/v1/vesting/schedules')
        return response.data
      } catch (error) {
        console.error('Erro ao listar vesting:', error)
        throw error
      }
    },
    criarSchedule: async (beneficiary, totalAmount, durationSecs, vestingType, revocable) => {
      try {
        const response = await apiClient.post('/api/v1/vesting/schedules', {
          beneficiary, total_amount: totalAmount, duration_secs: durationSecs,
          vesting_type: vestingType, revocable
        })
        return response.data
      } catch (error) {
        console.error('Erro ao criar vesting:', error)
        throw error
      }
    },
    calcularVested: async (scheduleId) => {
      try {
        const response = await apiClient.get(`/api/v1/vesting/schedules/${scheduleId}/vested`)
        return response.data
      } catch (error) {
        console.error('Erro ao calcular vested:', error)
        throw error
      }
    },
    claim: async (scheduleId) => {
      try {
        const response = await apiClient.post(`/api/v1/vesting/schedules/${scheduleId}/claim`)
        return response.data
      } catch (error) {
        console.error('Erro ao fazer claim:', error)
        throw error
      }
    }
  },

  // API de Rede P2P
  rede: {
    peers: async () => {
      try {
        const response = await apiClient.get('/api/v1/peers')
        return response.data
      } catch (error) {
        console.error('Erro ao obter peers:', error)
        throw error
      }
    },
    conectar: async (address, port) => {
      try {
        const response = await apiClient.post('/api/v1/peers/connect', { address, port })
        return response.data
      } catch (error) {
        console.error('Erro ao conectar peer:', error)
        throw error
      }
    },
    finality: async (slot) => {
      try {
        const response = await apiClient.get(`/api/v1/finality/${slot}`)
        return response.data
      } catch (error) {
        console.error('Erro ao verificar finality:', error)
        throw error
      }
    }
  },

  // API de Faucet
  faucet: {
    solicitar: async (address, amount) => {
      try {
        const response = await apiClient.post('/api/v1/faucet', { address, amount })
        return response.data
      } catch (error) {
        console.error('Erro ao solicitar faucet:', error)
        throw error
      }
    }
  },

  // API de Sync
  sync: {
    estado: async () => {
      try {
        const response = await apiClient.get('/api/v1/sync/state')
        return response.data
      } catch (error) {
        console.error('Erro ao obter estado sync:', error)
        throw error
      }
    },
    blocos: async (from, limit) => {
      try {
        const response = await apiClient.get(`/api/v1/sync/blocks?from=${from}&limit=${limit}`)
        return response.data
      } catch (error) {
        console.error('Erro ao obter blocos sync:', error)
        throw error
      }
    }
  },

  // API de Provas Merkle
  provas: {
    obterProva: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/proof/${address}`)
        return response.data
      } catch (error) {
        console.error('Erro ao obter prova Merkle:', error)
        throw error
      }
    }
  },

  // API de Token Sale
  tokenSale: {
    status: async (usarCache = true) => {
      const cacheKey = 'token_sale_status'
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get('/api/v1/token-sale/status')
        return cache.set(cacheKey, response.data, 15)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        return {
          remaining_nanokinx: 0,
          total_sold_nanokinx: 0,
          status: 'unknown',
          sale_pool_address: '',
          currency: window.__QUARNX_COIN__ || 'tKinX',
        }
      }
    },
    comprar: async (buyerAddress, amount, paymentCurrency, paymentAmount) => {
      try {
        const response = await apiClient.post('/api/v1/token-sale/buy', {
          buyer_address: buyerAddress,
          amount: amount || 0,
          payment_currency: paymentCurrency,
          payment_amount: paymentAmount || 0,
        })
        return response.data
      } catch (error) {
        if (error.response && error.response.data) {
          return error.response.data
        }
        console.error('Erro ao comprar KinX:', error)
        throw error
      }
    }
  },

  // API de Compilação Quartz-Lang
  compilador: {
    compilar: async (code, name) => {
      try {
        const response = await apiClient.post('/api/v1/compile', { code, name })
        return response.data
      } catch (error) {
        console.error('Erro ao compilar:', error)
        throw error
      }
    }
  },

  // API de Mérito PoSEM
  merito: {
    obterScores: async () => {
      try {
        const response = await apiClient.get('/api/v1/merit/scores')
        return response.data
      } catch (error) {
        return { scores: [], total: 0 }
      }
    },
    obterScoreValidador: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/merit/validator/${address}`)
        return response.data
      } catch (error) {
        return null
      }
    }
  },

  // API de Delegação
  delegacao: {
    delegar: async (delegator, validator, amount) => {
      try {
        const response = await apiClient.post('/api/v1/delegation/delegate', { delegator, validator, amount })
        return response.data
      } catch (error) {
        console.error('Erro ao delegar:', error)
        throw error
      }
    },
    desdelegar: async (delegator, delegationId, amount) => {
      try {
        const response = await apiClient.post('/api/v1/delegation/undelegate', { delegator, delegation_id: delegationId, amount })
        return response.data
      } catch (error) {
        console.error('Erro ao desdelegar:', error)
        throw error
      }
    },
    obterDelegacoes: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/delegation/delegator/${address}`)
        return response.data
      } catch (error) {
        return { delegations: [], total_delegated: 0 }
      }
    },
    obterRecompensas: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/delegation/rewards/${address}`)
        return response.data
      } catch (error) {
        return { rewards: [], total_rewards: 0 }
      }
    }
  },

  // ═══ Inovação QuarnX V2 ═══

  // API de Contribuição (PoC Layer)
  contribuicao: {
    obterTokens: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/contribution/tokens/${address}`)
        return response.data
      } catch (error) {
        return { tokens: [], total: 0 }
      }
    },
    obterLeaderboard: async () => {
      try {
        const response = await apiClient.get('/api/v1/contribution/leaderboard')
        return response.data
      } catch (error) {
        return { leaderboard: [], total: 0 }
      }
    }
  },

  // API de Referrals
  referrals: {
    obterCadeia: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/referrals/${address}`)
        return response.data
      } catch (error) {
        return { referrals: [], total_referred: 0 }
      }
    }
  },

  // API de Qualidade de Contratos
  qualidade: {
    obterQualidade: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/contracts/${address}/quality`)
        return response.data
      } catch (error) {
        return null
      }
    }
  },

  // API de Developer NFTs
  developerNFT: {
    obterNFT: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/developers/${address}/nft`)
        return response.data
      } catch (error) {
        return null
      }
    }
  },

  // API de Investigações Sybil
  sybil: {
    obterInvestigacoes: async () => {
      try {
        const response = await apiClient.get('/api/v1/sybil/investigations')
        return response.data
      } catch (error) {
        return { investigations: [], total: 0 }
      }
    }
  },

  // API de Rotação de Chave
  rotacao: {
    obterStatus: async (address) => {
      try {
        const response = await apiClient.get(`/api/v1/validators/${address}/rotation-status`)
        return response.data
      } catch (error) {
        return null
      }
    }
  },

  // API de Protocol Fee (QRC-21 v2)
  protocolFee: {
    obterStats: async (usarCache = true) => {
      const cacheKey = 'protocol_fee_stats'
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get('/api/v1/protocol-fee/stats')
        return cache.set(cacheKey, response.data, 15)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        return {
          balance: 0,
          total_distributed: 0,
          distribution_count: 0,
          fee_enabled_tokens: 0,
          last_epoch_fees: 0,
        }
      }
    }
  },

  // API de Treasury
  treasury: {
    obterDados: async (usarCache = true) => {
      const cacheKey = 'treasury_dados'
      if (usarCache) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
      }
      try {
        const response = await apiClient.get('/api/v1/treasury')
        return cache.set(cacheKey, response.data, 15)
      } catch (error) {
        const cachedData = cache.get(cacheKey)
        if (cachedData) return cachedData
        return {
          address: '',
          balance: 0,
          balance_kinx: 0,
          total_allocated: 0,
          cap: 0,
          allocations_count: 0,
          recent_allocations: [],
        }
      }
    }
  }
}

export { API_BASE_URL }
export default ExplorerAPI
